<template>
  <Header :title="$t('tenantManagement.title')" />
  <div v-if="readPermission" class="tenantManagementContainer">
    <div class="pageContainerJob">
      <div class="customRowTenant">
        <p class="jobHistoryTitle" style="text-align: left">{{ $t('tenantManagement.tenants') }}</p>
        <div class="searchBar">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
            <circle cx="6.5" cy="7.45996" r="5.75" stroke="#8F969B" stroke-width="1.5" />
            <path d="M11 11.96L15 15.96" stroke="#8F969B" stroke-width="1.5" stroke-linecap="round" />
          </svg>
          <input type="text" class="searchInput" :placeholder="$t('common.search')" v-model="search" @input="fetchTenants" />
        </div>
      </div>
      <div v-if="loading" style="width: 100%; display: flex; justify-content: center; align-items: center; height: 100%">
        <div class="spinner-border text-primary spinner-grow-xl" role="status"></div>
      </div>

      <!-- Search & Add Button -->
      <div v-if="!loading" class="tableHeadingsNew">
        <div class="tableHeadingsNew" style="margin-top: 20px">
          <div class="customRow">
            <img src="/backward.svg" :alt="$t('common.back')" style="cursor: pointer" @click="goToPreviousPage" />
            <span class="pageOfTitle">{{ $t('common.page') }} {{ page }} {{ $t('common.of') }} {{ totalPages }}</span>
            <img src="/forward.svg" :alt="$t('common.next')" style="cursor: pointer" @click="goToNextPage" />
            <div class="pageDiv" @click.stop="togglePageSizeDropdown">
              <span class="pageDivText">{{ pageSize }}</span>
              <img src="/arrowDownNew.png" class="arrowDownNew" alt="" />

              <ul ref="dropdownEl" v-if="showPageSizeDropdown" class="pageSizeDropdownNew" @click.stop>
                <li v-for="size in pageSizeOptions" :key="size" @click.stop="selectPageSize(size)">
                  {{ size }}
                </li>
              </ul>
            </div>
          </div>
        </div>

        <SecondaryButton v-if="writePermission && userRole === 'Super Admin'" @click="handleAddTenant">{{ $t('tenantManagement.add_new_tenant') }}</SecondaryButton>
      </div>

      <!-- Tenant Table -->
      <div v-if="!loading" class="tableContainer" style="margin-top: 5px">
        <table class="customTable">
          <thead class="customTableHeader">
            <tr class="customTableRow">
              <th style="padding-right: 20px">#</th>
              <th>{{ $t('tenantManagement.table.tenant_logo') }}</th>
              <th>{{ $t('tenantManagement.table.tenant_name') }}</th>
              <th v-if="userRole === 'Super Admin'">{{ $t('tenantManagement.table.is_active') }}</th>
              <th>{{ $t('tenantManagement.table.action') }}</th>
            </tr>
          </thead>
          <tbody class="customTableBody">
            <tr v-for="(tenant, index) in tenants" :key="tenant.id" style="height: 50px !important">
              <td>{{ index + 1 + (page - 1) * pageSize }}</td>
              <td>
                <img :src="tenant.logo || whiteLogo" :alt="$t('tenantManagement.table.tenant_logo')" width="60" height="32" style="object-fit: contain" />
              </td>
              <td>{{ tenant.tenant_name }}</td>
              <td v-if="userRole === 'Super Admin'">
                <div class="toggle-wrapper">
                  <CustomToggle v-if="writePermission" :modelValue="tenant.is_active" @update:modelValue="(value) => handleStatusToggle(tenant)" />
                  <span v-else>{{ tenant.is_active ? $t('common.yes') : $t('common.no') }}</span>
                </div>
              </td>
              <td>
                <span style="padding-right: 20px" v-if="userRole === 'Super Admin'">
                  <CustomHyperlink v-if="writePermission" @click="navigateToEdit(tenant)">{{ $t('tenantManagement.actions.configure') }}</CustomHyperlink>
                </span>
                <span style="padding-right: 20px">
                  <CustomHyperlink v-if="writePermission" @click="navigateToUsers(tenant)">{{ $t('tenantManagement.actions.users') }}</CustomHyperlink>
                </span>
                <span style="padding-right: 20px" v-if="userRole !== 'Super Admin'">
                  <CustomHyperlink v-if="writePermission" @click="openJobNotificationPopup(tenant)">{{ $t('tenantManagement.actions.job_notifications') }}</CustomHyperlink>
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
    </div>

    <ConfirmationModal v-if="showConfirmModal" :show="showConfirmModal" :message="$t('tenantManagement.confirm_status_change', { action: selectedTenant?.is_active ? $t('common.enable') : $t('common.disable'), tenantName: selectedTenant?.tenant_name })" @close="closeConfirmModal" @confirm="confirmStatusChange" />
    <JobNotificationPopup v-if="showJobNotificationPopup" :show="showJobNotificationPopup" :tenant="selectedTenant" @close="closeJobNotificationPopup" />
  </div>
  <div v-if="!readPermission">
    <PermissionAlert :message="$t('common.no_permission')" />
  </div>
</template>

<script>
import Header from '../../components/Header.vue'
import { get, put } from '@/utils/api'
import CustomToggle from '@/components/CustomToggle/CustomToggle.vue'
import CustomHyperlink from '@/components/CustomHyperlink/CustomHyperlink.vue'
import SecondaryButton from '@/components/SecondaryButton/SecondaryButton.vue'
import ConfirmationModal from '@/components/ConfirmationModal/ConfirmationModal.vue'
import PermissionAlert from '@/components/PermissionAlert.vue'
import JobNotificationPopup from '@/components/JobNotificationPopup/JobNotificationPopup.vue'
import whiteLogo from '@/assets/whiteLogo.png'
import { showToast } from '@/utils/toast'

export default {
  components: {
    Header,
    CustomToggle,
    CustomHyperlink,
    SecondaryButton,
    ConfirmationModal,
    PermissionAlert,
    JobNotificationPopup,
  },
  data() {
    return {
      loading: false,
      tenants: [],
      search: '',
      page: 1,
      pageSize: 10,
      total: 0,
      totalPages: 1,
      pageSizeOptions: [10, 20, 50, 100],
      showPageSizeDropdown: false,
      showConfirmModal: false,
      showJobNotificationPopup: false,
      selectedTenant: null,
      pendingStatusChange: null,
      whiteLogo,
      readPermission: false,
      writePermission: false,
      userRole: '',
    }
  },
  mounted() {
    document.title = 'Tenant Management'
    this.checkPermissions()
    this.fetchTenants()
    document.addEventListener('click', this.handleClickAway)
    const userRolesString = localStorage.getItem('UserRoles')
    const userRoles = JSON.parse(userRolesString)
    this.userRole = userRoles[0]?.role?.roleName || ''
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickAway)
  },
  methods: {
    checkPermissions() {
      const permissions = JSON.parse(localStorage.getItem('permissions') || '[]')
      this.readPermission = permissions.some((p) => p.object_name === 'TENANTS_RD' && p.is_active)
      this.writePermission = permissions.some((p) => p.object_name === 'TENANTS_WR' && p.is_active)
    },
    handleClickAway(event) {
      const dropdownEl = this.$refs.dropdownEl
      if (dropdownEl && !dropdownEl.contains(event.target)) {
        this.showPageSizeDropdown = false
        this.showEditCategoryDropdown = false
      }
    },
    async fetchTenants() {
      this.loading = true
      try {
        const response = await get(`/v1/tenants?page=${this.page}&pageSize=${this.pageSize}&search=${this.search}&showLogo=true`)
        if (response?.data?.code === 200) {
          this.tenants = response.data.data.tenants
          this.total = response.data.data.total
          this.totalPages = Math.ceil(this.total / this.pageSize)
        }
      } catch (err) {
        showToast({ title: 'Error', message: 'Failed to fetch tenants.', type: 'error' })
      } finally {
        this.loading = false
      }
    },
    handleAddTenant() {
      const tenantName = localStorage.getItem('url_name')
      this.$router.push(`/${tenantName}/tenant/add`)
    },
    togglePageSizeDropdown() {
      this.showPageSizeDropdown = !this.showPageSizeDropdown
    },
    async selectPageSize(size) {
      this.pageSize = size
      this.page = 1
      this.showPageSizeDropdown = false
      await this.fetchTenants()
    },
    async goToPreviousPage() {
      if (this.page > 1) {
        this.page--
        await this.fetchTenants()
      }
    },
    async goToNextPage() {
      if (this.page < this.totalPages) {
        this.page++
        await this.fetchTenants()
      }
    },
    navigateToUsers(tenant) {
      const tenantName = localStorage?.getItem('url_name') || ''
      this.$router.push(`/${tenantName}/users-management?tenantId=${tenant.id}`)
    },
    navigateToEdit(tenant) {
      const tenantName = localStorage?.getItem('url_name') || ''
      this.$router.push(`/${tenantName}/tenant/edit/${tenant.id}`)
      // this.$router.push(`/edit-tenant/${tenant.id}`)
    },
    handleStatusToggle(tenant) {
      this.selectedTenant = { ...tenant, is_active: !tenant.is_active }
      this.showConfirmModal = true
    },
    closeConfirmModal() {
      this.showConfirmModal = false
    },
    async confirmStatusChange() {
      const payload = this.selectedTenant

      try {
        const response = await put(`/v1/tenants/${payload.id}`, payload)

        if (response.data.code === 200) {
          await this.fetchTenants()
          showToast({
            title: 'Success',
            message: 'Tenant status updated successfully.',
            type: 'success',
          })
        } else {
          showToast({
            title: 'Error',
            message: 'Failed to update tenant status.',
            type: 'error',
          })
        }
      } catch (error) {
        showToast({
          title: 'Error',
          message: 'Failed to update tenant status.',
          type: 'error',
        })
      } finally {
        this.closeConfirmModal()
      }
    },
    openJobNotificationPopup(tenant) {
      this.selectedTenant = tenant
      this.showJobNotificationPopup = true
    },
    closeJobNotificationPopup() {
      this.showJobNotificationPopup = false
      this.selectedTenant = null
    },
  },
}
</script>

<style scoped>
.pageSizeDropdownNew {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 1000;
  background: #fff;
  border: 1px solid #ccc;
  border-radius: 4px;
  margin-top: 4px;
  list-style: none;
  padding: 4px 0;
  width: 100%;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

.pageSizeDropdownNew li {
  padding: 6px 12px;
  cursor: pointer;
  font-family: 'Archivo', sans-serif;
  font-size: 14px;
  color: #5c656c;
}

.pageSizeDropdownNew li:hover {
  background: #f0f0f0;
}

.pageDiv {
  position: relative;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #ccc;
  padding: 6px 12px;
  border-radius: 4px;
  background: #fff;
}

.tableContainer {
  max-height: 65vh;
  overflow-y: auto;
}
</style>
